import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import {
  Checkbox,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  notification,
  Tooltip,
  Typography
} from 'antd'
import type { RangePickerProps } from 'antd/es/date-picker'

import moment from 'moment'
import styled from 'styled-components'
import { colours, sizes } from '@qflow/theme'

import { SelectMeter, UTILITIES } from 'portal/components/Utilities/common'
import { AddEditMeterModal } from 'portal/components/Utilities/Meters/AddEditMeterModal'

import { Meter } from 'portal/components/Utilities/Meters/Interface'
import { Reading } from './Interface'

import { ColourButton, icons, Spacer } from 'portal/lib/primitives'

import { addMeter } from 'portal/store/utilities/meters/actions'
import { trackMixpanel } from 'portal/store/mixpanel/actions'
import { addReading } from 'portal/store/utilities/readings/actions'

import { READING_VALIDATION_MESSAGES } from './constants'
import { getClosestDatesIndices } from './functions'

interface NewReadingPanelProps {
  meters: Meter[]
  readings: Reading[]
  selectedMeterId: string
  selectedUtility: string
  setShowNewReadingsPanel: (show: boolean) => void
  metersReadOnly: boolean
  metersReload: () => void
  readingsReload: () => void
}

export const NewReadingPanel = (props: NewReadingPanelProps) => {
  const [selectedMeterId, setSelectedMeterId] = useState(props.selectedMeterId)
  const [newMeterModalOpen, setNewMeterModalOpen] = useState(false)
  const [newlyCreatedMeterRef, setNewlyCreatedMeterRef] = useState<
    string | null
  >()

  const [fieldReadingDate, setFieldReadingDate] = useState<
    string | undefined | null
  >(undefined)
  const [fieldReading, setFieldReading] = useState<number | null>()
  const [fieldEstimate, setFieldEstimate] = useState<boolean>(false)
  const [fieldRolledOver, setFieldRolledOver] = useState<boolean>(false)
  const [fieldNotes, setFieldNotes] = useState<string>('')

  const [saving, setSaving] = useState<boolean>(false)

  const [readingValidationMessage, setReadingValidationMessage] =
    useState<string>('')

  const { accountId, projectId } = useParams<{
    accountId: string
    projectId: string
  }>()

  const dispatch = useDispatch()
  const { Title } = Typography
  const { TextArea } = Input

  const EV_METER_ADD = 'frontend_utilities_readings_meter_added'
  const trackMeterAdd = useCallback(() => {
    dispatch(
      trackMixpanel(EV_METER_ADD, {
        project_id: projectId,
        account_id: accountId
      })
    )
  }, [accountId, dispatch, projectId])

  const EV_READING_ADD = 'frontend_utilities_readings_reading_added'
  const trackReadingAdd = useCallback(() => {
    dispatch(
      trackMixpanel(EV_READING_ADD, {
        project_id: projectId,
        account_id: accountId
      })
    )
  }, [accountId, dispatch, projectId])

  const handleAddMeterSubmit = (
    meterName: string,
    meterReference: string,
    utilityType: string,
    showSuccessNotification: () => void,
    showFailureNotification: () => void
  ) => {
    // Add meter
    dispatch(
      addMeter(
        accountId,
        projectId,
        meterName,
        meterReference,
        utilityType,
        props.metersReload,
        showSuccessNotification,
        showFailureNotification
      )
    )
    trackMeterAdd()

    setNewMeterModalOpen(false)
    setNewlyCreatedMeterRef(meterReference)
  }

  const showSuccessNotification = () => {
    notification.success({
      message: 'Reading successfully added.',
      duration: 5,
      placement: 'top'
    })

    props.setShowNewReadingsPanel(false)
    setTimeout(() => {
      setSaving(false)
      props.setShowNewReadingsPanel(true)
    }, 10)
  }

  const showFailureNotification = () => {
    notification.error({
      message: 'Reading failed to be added.',
      description: 'Please check the values and try again.',
      duration: 10,
      placement: 'top'
    })

    setSaving(false)
  }

  const handleSaveReading = () => {
    setSaving(true)
    dispatch(
      addReading(
        accountId,
        projectId,
        selectedMeterId,
        fieldReadingDate as string,
        fieldReading as number,
        fieldNotes,
        fieldEstimate,
        fieldRolledOver,
        props.readingsReload,
        showSuccessNotification,
        showFailureNotification
      )
    )
    trackReadingAdd()
  }

  const handleClose = () => {
    if (fieldReadingDate || fieldReading || fieldNotes) {
      Modal.confirm({
        title: "Closing the 'New reading' panel",
        content: 'This will clear all entered values. Are you sure?',
        autoFocusButton: null,
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk() {
          props.setShowNewReadingsPanel(false)
        }
      })
    } else {
      props.setShowNewReadingsPanel(false)
    }
  }

  useEffect(() => {
    if (!selectedMeterId && newlyCreatedMeterRef) {
      const newlyCreatedMeter = props.meters.find(
        meter => meter.meterReference === newlyCreatedMeterRef
      )
      if (newlyCreatedMeter) {
        setSelectedMeterId(newlyCreatedMeter.meterId as string)
      }
    }
  }, [newlyCreatedMeterRef, props.meters, selectedMeterId])

  const filteredReadings = useMemo(
    () =>
      selectedMeterId
        ? props.readings.filter(reading => reading.meterId === selectedMeterId)
        : [],
    [selectedMeterId, props.readings]
  )

  const dynamicTitle = useMemo(() => {
    if (!selectedMeterId) {
      return 'New reading'
    }

    const selectedmMeter = props.meters.find(
      (meter: Meter) => meter.meterId === selectedMeterId
    )
    return (
      <div>
        {selectedmMeter?.meterName}{' '}
        <StyledMeterRef>{selectedmMeter?.meterReference}</StyledMeterRef>
      </div>
    )
  }, [selectedMeterId, props.meters])

  const validateNewReading = useMemo(() => {
    // Return true if estimate is checked
    if (fieldEstimate) {
      setReadingValidationMessage('')
      return true
    }

    // Check that fieldReadingDate does not already exist in filteredReadings
    if (
      filteredReadings.some(
        reading =>
          reading.readingDateTime.substring(0, 10) ===
          fieldReadingDate?.substring(0, 10)
      )
    ) {
      setReadingValidationMessage(READING_VALIDATION_MESSAGES.DUPLICATE)
      return false
    }

    // Check that fieldReadingDate is not in the future
    if (fieldReadingDate && moment(fieldReadingDate).isAfter(moment())) {
      setReadingValidationMessage(READING_VALIDATION_MESSAGES.FUTURE_DATE)
      return false
    }

    // fieldReading is greater than the closest, non-Estimated preceeding reading in filteredReadings
    // fieldReading is less than the closest, non-Estimated postceeding reading in filteredReadings
    const filteredReadingsNoEstimated = filteredReadings.filter(
      reading => !reading.isEstimated
    )

    if (
      filteredReadingsNoEstimated.length > 0 &&
      fieldReadingDate &&
      (fieldReading || fieldReading === 0)
    ) {
      const closestDatesIndexes = getClosestDatesIndices(
        filteredReadingsNoEstimated.map(reading => reading.readingDateTime),
        fieldReadingDate
      )

      if (closestDatesIndexes.beforeIndex > -1) {
        if (
          fieldReading <
            filteredReadingsNoEstimated[closestDatesIndexes.beforeIndex]
              .actualValue &&
          !fieldRolledOver
        ) {
          setReadingValidationMessage(
            `${READING_VALIDATION_MESSAGES.TOO_LOW} ${filteredReadingsNoEstimated[closestDatesIndexes.beforeIndex].actualValue}.`
          )
          return false
        }
      }

      if (closestDatesIndexes.afterIndex > -1) {
        if (
          fieldReading >
            filteredReadingsNoEstimated[closestDatesIndexes.afterIndex]
              .actualValue &&
          !filteredReadingsNoEstimated[closestDatesIndexes.afterIndex]
            .hasRolledOver
        ) {
          setReadingValidationMessage(
            `${READING_VALIDATION_MESSAGES.TOO_HIGH} ${filteredReadingsNoEstimated[closestDatesIndexes.afterIndex].actualValue}.`
          )
          return false
        }
      }
    }

    setReadingValidationMessage('')
    return true
  }, [
    fieldEstimate,
    fieldReading,
    fieldReadingDate,
    fieldRolledOver,
    filteredReadings
  ])

  const disabledDate: RangePickerProps['disabledDate'] = current => {
    // Can not select days before today and today
    return current && current < moment('1999-12-31').endOf('day')
  }

  const saveDisabled = useMemo(() => {
    return (
      !selectedMeterId ||
      !fieldReadingDate ||
      fieldReading === undefined ||
      fieldReading === null ||
      fieldReading < 0 ||
      !validateNewReading ||
      saving
    )
  }, [
    selectedMeterId,
    fieldReadingDate,
    fieldReading,
    validateNewReading,
    saving
  ])

  return (
    <NewReadingSection>
      <TitleBar>
        <Title level={4}>{dynamicTitle}</Title>
        <CloseButton id="newReadingCloseBtn" onClick={handleClose}>
          <icons.Cross colour={colours.BLACK} size={sizes.SMALL} />
        </CloseButton>
      </TitleBar>
      <SubSection>
        <SubSectionPartWrapper>
          {!props.selectedMeterId && (
            <SubSectionPart>
              <Title level={5}>Meter information</Title>

              <Block>
                <div>
                  <SelectMeter
                    selectedMeterId={selectedMeterId}
                    setSelectedMeterId={setSelectedMeterId}
                    meters={props.meters}
                    showLabel={true}
                  />
                </div>

                <Tooltip
                  title={
                    props.metersReadOnly
                      ? 'You do not have permission to add or edit meters.'
                      : null
                  }
                  placement="left"
                >
                  <div>
                    <ColourButton.Basic
                      id="NewMeter"
                      onClick={() => setNewMeterModalOpen(true)}
                      disabled={props.metersReadOnly}
                    >
                      <icons.Plus size={sizes.SMALL} />
                      <Spacer.Fine />
                      New meter
                    </ColourButton.Basic>
                  </div>
                </Tooltip>
              </Block>
            </SubSectionPart>
          )}

          <SubSectionPart style={{ flexGrow: 1 }}>
            <Title level={5}>Reading</Title>
            <Block>
              <Fields>
                <FieldWrapper style={{ flexBasis: '16.5rem' }}>
                  <StyledLabel htmlFor="readingDate">Reading date</StyledLabel>
                  <DatePicker
                    name="readingDate"
                    id="readingDate"
                    format={'DD/MM/YYYY'}
                    style={{ width: '100%' }}
                    defaultValue={
                      fieldReadingDate
                        ? moment(fieldReadingDate, 'DD/MM/YYYY')
                        : undefined
                    }
                    disabledDate={disabledDate}
                    onChange={value =>
                      setFieldReadingDate(value?.format('YYYY-MM-DD'))
                    }
                  />
                </FieldWrapper>
                <FieldWrapper style={{ flexBasis: '16.5rem' }}>
                  <StyledLabel htmlFor="reading">
                    Reading (
                    {props.selectedUtility === UTILITIES.WATER ? (
                      <span>
                        m<sup>3</sup>
                      </span>
                    ) : (
                      'kWh'
                    )}
                    )
                  </StyledLabel>
                  <InputNumber
                    name="reading"
                    id="reading"
                    type="number"
                    controls={false}
                    style={{ width: '100%' }}
                    value={fieldReading}
                    onChange={value => setFieldReading(value)}
                  />
                </FieldWrapper>
                <FieldWrapper>
                  <StyledLabel htmlFor="estimate">Estimate</StyledLabel>
                  <StyledCheckbox
                    name="estimate"
                    id="estimate"
                    checked={fieldEstimate}
                    onChange={event => setFieldEstimate(event.target.checked)}
                  >
                    Yes
                  </StyledCheckbox>
                </FieldWrapper>
                <FieldWrapper style={{ flex: '0 0 4.75rem', display: 'none' }}>
                  <StyledLabel htmlFor="rolledOver">Rolled over</StyledLabel>
                  <StyledCheckbox
                    name="rolledOver"
                    id="rolledOver"
                    checked={fieldRolledOver}
                    disabled={filteredReadings.length === 0}
                    onChange={event => setFieldRolledOver(event.target.checked)}
                  >
                    Yes
                  </StyledCheckbox>
                </FieldWrapper>
                <FieldWrapper style={{ flexBasis: '25rem' }}>
                  <StyledLabel htmlFor="notes">
                    Notes <Fade>(optional)</Fade>
                  </StyledLabel>
                  <TextArea
                    name="notes"
                    id="notes"
                    rows={1}
                    value={fieldNotes}
                    onChange={event => setFieldNotes(event.target.value)}
                    maxLength={200}
                    style={{ flexGrow: 1 }}
                  />
                </FieldWrapper>
              </Fields>
            </Block>
          </SubSectionPart>
        </SubSectionPartWrapper>
        <Block
          style={{
            justifyContent: readingValidationMessage
              ? 'space-between'
              : 'flex-end',
            alignItems: 'center',
            marginBottom: 0
          }}
        >
          {readingValidationMessage && (
            <ErrorContainer>
              <icons.CrossCircle
                size={sizes.SMALL_MEDIUM}
                colour={colours.RED}
              />
              <div>{readingValidationMessage}</div>
            </ErrorContainer>
          )}
          <ColourButton.Action
            id="NewReading"
            onClick={handleSaveReading}
            disabled={saveDisabled}
          >
            Save reading
          </ColourButton.Action>
        </Block>
      </SubSection>
      {newMeterModalOpen && (
        <AddEditMeterModal
          defaultUtilityType={props.selectedUtility}
          meter={null}
          isOpen={newMeterModalOpen}
          onCancel={() => setNewMeterModalOpen(false)}
          onSubmit={handleAddMeterSubmit}
          onDelete={() => {}}
        />
      )}
    </NewReadingSection>
  )
}

const NewReadingSection = styled.div`
  padding: 1.25rem 1.25rem 1.5rem 1.375rem;
  margin: 0 0 1.25rem -0.5rem;
  box-shadow: 0px 1px 3px 0px #00000026;
  background-color: ${colours.WHITE};
  border-radius: 10px;

  h4 {
    font-size: 1.125rem;
    margin: 0 0 1rem;
  }
`
const TitleBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`
const StyledMeterRef = styled.span`
  font-size: 0.875rem;
  font-weight: 400;
`
const CloseButton = styled.div`
  cursor: pointer;
  color: ${colours.CORE_NAVY};
  width: 40px;
  height: 40px;
  margin-top: -0.375rem;
  border-radius: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${colours.CONTROL_PANEL_GREY};
  }
`
const SubSection = styled.div`
  padding: 1rem 1.25rem 1.125rem;
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 10px;

  h5 {
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.375rem;
    margin: 0 0 0.25rem;
  }
`
const SubSectionPartWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0 1rem;
`
const SubSectionPart = styled.div`
  margin: 0;
`
const Block = styled.div`
  margin: 0 0 1.5rem;
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  width: 100%;
`
const Fields = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 1rem;
  width: 100%;
`
const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
`
const StyledLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
`
const Fade = styled.span`
  color: ${colours.CHARCOAL_GREY};
  font-size: 0.75rem;
`
const StyledCheckbox = styled(Checkbox)`
  margin-top: 0.375rem;
`
const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  color: ${colours.CORE_NAVY};
  background-color: ${colours.RED}11;
  border-radius: 5px;
  border: 1px solid ${colours.RED}22;
  padding: 0.25rem 1rem 0.25rem 0.75rem;
`
